import "./App.scss";
import { StepComponent } from "./Components/LandingpageComponents/Wizard/StepComponent/StepComponent";
import ErrorBoundary from "./Components/SharedComponents/ErrorBoundary/ErrorBoundary";
import HeroBanner from "./Components/LandingpageComponents/HeroBanner/Herobanner";
import { useEffect, useState } from "react";
import NotFound from "./Components/SharedComponents/NotFound/NotFound";
import TokenContext from "./Contexts/TokenContext";
import PartnerContentContext from "./Contexts/PartnerContentContext";
import { PartnerContent } from "./Models/PartnerContentModel";
import { getPartnerContentObject } from "./Services/ApiService";
import Overlay from "./Components/LandingpageComponents/Wizard/Overlay/overlay";
import OverlayModel from "./Models/OverlayModel";
import { Route, Switch, useHistory } from "react-router-dom";
import NorlysErrorPage from "./Components/SharedComponents/ErrorPage/NorlysErrorPage";
import Redeempage from "./Components/RedeempageComponents/Redeempage/Redeempage";
import AddressModel from "./Models/AddressModel";
import { StepEnum } from "./Models/StepEnum";
import { ThemeProvider } from "@mui/material";
import theme from "./Themes/theme";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import "dayjs/locale/da";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { fetchConsentText } from "./Services/ConsentService";

interface PartnerData {
	title: string;
	description: string;
	isCrawlable: boolean;
}

function App() {
	const [bannerHidden, setBannerHidden] = useState(false);
	const [validPartner, setValidPartner] = useState(false);
	const [token, setToken] = useState("");
	const [partnerContent, setPartnerContent] = useState(new PartnerContent());
	const [overlayObject, setOverlayObject] = useState<OverlayModel>(
		new OverlayModel(true, "", true)
	);
	const [address, setAddress] = useState<AddressModel>(new AddressModel());
	const [step, setStep] = useState(StepEnum.NameAddressStep);
	const [partnerMetaData, setPartnerMetaData] = useState<PartnerData | null>(null);

	let history = useHistory();

  const fixPlaceholdersInText = (res: PartnerContent) => {
    const voucherValueNewSale = res.voucherValueNewSale;
    const voucherValueResale = res.voucherValueResale;
    const outboundSla = res.outboundSla;

    let json = JSON.stringify(res)
      .replaceAll("{voucherValueNewSale}", voucherValueNewSale?.toString())
      .replaceAll("{voucherValueResale}", voucherValueResale?.toString())
      .replaceAll("{outboundSla}", outboundSla?.toString());
    return JSON.parse(json) as PartnerContent;
  };

  const checkCleanPartnerName = (input: string) => {
    return input.replaceAll("%20", " ");
  };

	function capitalizeFirstLetter(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	function updatePartnerMetaData(partnerId: string) {
		if (partnerId.toLowerCase() === "tivoli") {
			setPartnerMetaData({
				title: `Skift elaftale til Norlys og få et Tivolikort Sølv`,
				description: `Få et Tivolikort Sølv til en værdi af 849 kr. når du skifter din elaftale til Norlys. Gælder kun nye kunder hvor Norlys ikke i forvejen leverer el på adressen.`,
				isCrawlable: true,
			});
		} else {
			setPartnerMetaData({
				title: `Norlys x ${capitalizeFirstLetter(partnerId)}`,
				description: ``,
				isCrawlable: false,
			});
		}
	}

	useEffect(() => {
		var pathUrl = window.location.pathname.substring(1);
		var splitstring = pathUrl.split("/", 1);
		var partner = splitstring[0];
		if (partner) {
			setOverlayObject(new OverlayModel(true, "", true));
			getPartnerContentObject(partner).then((res) => {
				if (res.isValid) {
					if (checkCleanPartnerName(partner) !== res.partnerId) {
						partner = res.partnerId;
						splitstring[1]
							? history.push(`/${res.partnerId}/${splitstring[1]}`)
							: history.push(`/${res.partnerId}`);
					}
					setPartnerContent(fixPlaceholdersInText(res));
					setValidPartner(true);
					updatePartnerMetaData(res.partnerId);
				} else {
					setValidPartner(false);
				}
				setOverlayObject(new OverlayModel(false, "", false));
			});
		} else {
			setValidPartner(false);
			setOverlayObject(new OverlayModel(false, "", false));
		}
		const handleRecaptcha = (e: Event) => {
			let event = e as CustomEvent;
			if (event && event.detail) {
				setToken(event.detail);
			}
		};
		window.addEventListener("googleRecaptcha", handleRecaptcha);

		fetchConsentText();

		return () => {
			window.removeEventListener("googleRecaptcha", handleRecaptcha);
		};
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<HelmetProvider>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="da">
			<ThemeProvider theme={theme}>
				<div>
					<Overlay
						active={overlayObject.overlayActive}
						message={overlayObject.overlayMessage}
						showLoader={overlayObject.overlayLoader}></Overlay>
					{!overlayObject.overlayActive && validPartner && (
						<div className="app-container">
							<ErrorBoundary>
								<PartnerContentContext.Provider value={partnerContent}>
									{partnerMetaData && (
										<Helmet>
											<title>{partnerMetaData.title}</title>
											<meta name="description" content={partnerMetaData.description} />
											<meta
												name="robots"
												content={
													partnerMetaData.isCrawlable ? "index, follow" : "noindex, nofollow"
												}
											/>
										</Helmet>
									)}
									<Switch>
										<Route path={`/${partnerContent.partnerId}`} exact>
											{!bannerHidden && <HeroBanner></HeroBanner>}
											<TokenContext.Provider value={token}>
												<StepComponent
													hideBanner={setBannerHidden}
													address={address}
													setAddress={setAddress}
													step={step}
													setStep={setStep}></StepComponent>
											</TokenContext.Provider>
										</Route>
										<Route
											path={`/${partnerContent.partnerId}/redeem/:partnerGuid`}
											exact
											component={Redeempage}
										/>
										<Route path={`/${partnerContent.partnerId}/:errorType`} exact>
											<NorlysErrorPage
												address={address}
												step={step}
											/>
										</Route>
										<Route component={NotFound} />
									</Switch>
								</PartnerContentContext.Provider>
							</ErrorBoundary>
						</div>
					)}
					{!overlayObject.overlayActive && !validPartner && <NotFound />}
				</div>
			</ThemeProvider>
			</LocalizationProvider>
		</HelmetProvider>
	);
}

export default App;
